import { useState } from 'react';
import type { RadioProgram, ProgramType } from '@/mocks/adminSchedule';
import { dayNames, programTypeLabels, programTypeColors } from '@/mocks/adminSchedule';
import ProgramCard from './ProgramCard';

interface ListViewProps {
  programs: RadioProgram[];
  onEdit?: (program: RadioProgram) => void;
  onDelete?: (program: RadioProgram) => void;
  onDuplicate?: (program: RadioProgram) => void;
  onQuickTimeChange?: (program: RadioProgram) => void;
  onToggleActive?: (program: RadioProgram) => void;
  onCancel?: (program: RadioProgram) => void;
}

export default function ListView({ programs, onEdit, onDelete, onDuplicate, onQuickTimeChange, onToggleActive, onCancel }: ListViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [dayFilter, setDayFilter] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState<ProgramType | null>(null);

  const filtered = programs.filter((p) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !p.host.toLowerCase().includes(q)) return false;
    }
    if (dayFilter !== null && p.dayOfWeek !== dayFilter) return false;
    if (typeFilter && p.type !== typeFilter) return false;
    return true;
  });

  const grouped = filtered.reduce((acc: Record<string, RadioProgram[]>, p) => {
    const key = p.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const labelA = programTypeLabels[a as ProgramType];
    const labelB = programTypeLabels[b as ProgramType];
    return labelA.localeCompare(labelB);
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar programa o locutor..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-background-50 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors placeholder:text-foreground-300"
          />
        </div>

        {/* Day filter */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setDayFilter(null)}
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all ${
              dayFilter === null ? 'bg-primary-500 text-white' : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100'
            }`}
          >
            Todos
          </button>
          {[0, 1, 2, 3, 4, 5, 6].map((d) => (
            <button
              key={d}
              onClick={() => setDayFilter(dayFilter === d ? null : d)}
              className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all ${
                dayFilter === d ? 'bg-primary-500 text-white' : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100'
              }`}
            >
              {dayNames[d].substring(0, 3)}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setTypeFilter(null)}
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all ${
              typeFilter === null ? 'bg-secondary-500 text-white' : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100'
            }`}
          >
            <i className="ri-filter-3-line text-[10px] mr-0.5"></i>
            Tipos
          </button>
          {(Object.keys(programTypeLabels) as ProgramType[]).map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(typeFilter === t ? null : t)}
              className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer transition-all ${
                typeFilter === t
                  ? `${programTypeColors[t].bg.replace('bg-', 'bg-').replace('-100', '-400')} text-white`
                  : `${programTypeColors[t].bg} ${programTypeColors[t].text}`
              }`}
            >
              <i className={`${programTypeColors[t].icon} text-[10px]`}></i>
              {programTypeLabels[t]}
            </button>
          ))}
        </div>
      </div>

      {/* Results summary */}
      <p className="text-foreground-400 text-xs mb-4">
        {filtered.length} programa{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* Grouped list */}
      <div className="space-y-6">
        {sortedTypes.length === 0 && (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-search-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm font-medium">Sin resultados</p>
            <p className="text-foreground-400 text-xs">Prueba con otros filtros</p>
          </div>
        )}

        {sortedTypes.map((type) => {
          const typePrograms = grouped[type];
          const colors = programTypeColors[type as ProgramType];

          return (
            <div key={type}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text}`}>
                  <i className={`${colors.icon} text-[10px]`}></i>
                  {programTypeLabels[type as ProgramType]}
                </span>
                <span className="text-foreground-400 text-[10px]">{typePrograms.length} programa{typePrograms.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2 pl-0">
                {typePrograms.map((p) => (
                  <ProgramCard
                    key={p.id}
                    program={p}
                    compact
                    onEdit={() => onEdit?.(p)}
                    onDelete={() => onDelete?.(p)}
                    onDuplicate={() => onDuplicate?.(p)}
                    onQuickTimeChange={() => onQuickTimeChange?.(p)}
                    onToggleActive={() => onToggleActive?.(p)}
                    onCancel={() => onCancel?.(p)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}